/**
 * Authentication Context Provider
 *
 * Manages user session state across the application using JWT tokens.
 * Handles login, logout, registration, and organization management.
 */

import React, { useState, useEffect, useCallback } from 'react'
import * as authApi from './api/auth'
import * as orgApi from './api/organizations'
import { hasToken, clearTokens } from './api/client'
import { AuthContext } from './auth-context'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [currentOrg, setCurrentOrgState] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isNewSignup, setIsNewSignup] = useState(false)

  // Fetch organizations for the current user
  const fetchOrganizations = useCallback(async () => {
    try {
      console.log('[Auth] Fetching organizations')
      const orgs = await orgApi.listOrganizations()
      console.log('[Auth] Organizations loaded:', orgs)
      setOrganizations(orgs)

      // Set current org from localStorage or first organization
      const savedOrgId = localStorage.getItem('currentOrganizationId')
      const savedOrg = orgs.find((org) => org.id === savedOrgId)

      if (savedOrg) {
        setCurrentOrgState(savedOrg)
        setUserRole(savedOrg.role_code)
      } else if (orgs.length > 0) {
        setCurrentOrgState(orgs[0])
        setUserRole(orgs[0].role_code)
        localStorage.setItem('currentOrganizationId', orgs[0].id)
      } else {
        setCurrentOrgState(null)
        setUserRole(null)
        localStorage.removeItem('currentOrganizationId')
      }
    } catch (error) {
      console.error('[Auth] Organizations fetch error:', error)
      setOrganizations([])
      setCurrentOrgState(null)
      setUserRole(null)
    }
  }, [])

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check if we have a token
      if (!hasToken()) {
        console.log('[Auth] No token found')
        setLoading(false)
        return
      }

      try {
        // Validate token by fetching current user
        console.log('[Auth] Validating token...')
        const userData = await authApi.getCurrentUser()
        console.log('[Auth] User loaded:', userData.email)
        setUser(userData)

        // Fetch organizations
        await fetchOrganizations()
      } catch (error) {
        console.error('[Auth] Token validation failed:', error)
        // Token is invalid, clear it
        clearTokens()
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth()

    // Listen for unauthorized events (401 responses)
    const handleUnauthorized = () => {
      console.log('[Auth] Received unauthorized event')
      setUser(null)
      setOrganizations([])
      setCurrentOrgState(null)
      setUserRole(null)
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [fetchOrganizations])

  // Login function
  const login = async (email, password) => {
    console.log('[Auth] Logging in...')
    const response = await authApi.login({ email, password })

    if (response.user) {
      setUser(response.user)
      await fetchOrganizations()
    }

    console.log('[Auth] Login successful')
    return response
  }

  // Register function
  const register = async (email, password, fullName = null) => {
    console.log('[Auth] Registering...')
    const response = await authApi.register({ email, password, fullName })

    if (response.user) {
      setUser(response.user)
      setIsNewSignup(true)
      // Clear new signup flag after 5 minutes
      setTimeout(() => setIsNewSignup(false), 5 * 60 * 1000)

      // Fetch organizations - user might have been auto-added via invitation
      await fetchOrganizations()
    }

    console.log('[Auth] Registration successful')
    return response
  }

  // Sign out function
  const handleSignOut = async () => {
    try {
      console.log('[Auth] Signing out...')
      await authApi.logout()
      console.log('[Auth] Sign out successful')
    } catch (error) {
      console.error('[Auth] Error signing out:', error)
    } finally {
      // Always clear state, even if API call fails
      setUser(null)
      setOrganizations([])
      setCurrentOrgState(null)
      setUserRole(null)
      localStorage.removeItem('currentOrganizationId')
    }
  }

  // Switch current organization
  const handleSetCurrentOrg = (org) => {
    setCurrentOrgState(org)
    setUserRole(org?.role_code || null)
    if (org) {
      localStorage.setItem('currentOrganizationId', org.id)
    } else {
      localStorage.removeItem('currentOrganizationId')
    }
  }

  // Refresh organizations
  // Note: Don't check for user state here as it may not be updated yet after registration
  const refreshOrganizations = async () => {
    if (hasToken()) {
      await fetchOrganizations()
    }
  }

  // Check if user has specific permission in current organization
  // Role hierarchy: owner > administrator > designer > field_agent
  // Normalize legacy role names
  const normalizeRole = (role) => {
    const roleAliases = { viewer: 'field_agent', survey_designer: 'designer' }
    return roleAliases[role] || role
  }

  const hasPermission = (permission) => {
    if (!currentOrg || !userRole) return false

    const normalizedRole = normalizeRole(userRole)

    // Owner has all permissions
    if (normalizedRole === 'owner') {
      return true
    }

    // Administrator has all permissions except org deletion and billing
    if (normalizedRole === 'administrator') {
      const adminRestrictedPermissions = ['org_delete', 'org_manage_billing']
      if (adminRestrictedPermissions.includes(permission)) {
        return false
      }
      return true
    }

    // Define permission mappings for other roles
    const rolePermissions = {
      // Designer: Can create/edit/delete surveys, view analytics, view responses
      designer: [
        'survey_create',
        'survey_edit',
        'survey_delete',
        'survey_view',
        'response_submit',
        'response_view_all',
        'response_view_own',
        'analytics_view',
      ],
      // Field Agent: Can view assigned surveys, submit responses, view own stats
      field_agent: ['survey_view', 'response_submit', 'response_view_own', 'analytics_view_own'],
    }

    const permissions = rolePermissions[normalizedRole] || []
    return permissions.includes(permission)
  }

  // Check if current organization has access to a feature
  const checkFeatureAccess = (feature) => {
    if (!currentOrg) return false

    const planCode = currentOrg.subscription_plan_code || 'standard'
    return orgApi.hasFeature(planCode, feature)
  }

  const value = {
    user,
    organizations,
    currentOrg,
    userRole,
    loading,
    isNewSignup,
    login,
    register,
    signOut: handleSignOut,
    setCurrentOrg: handleSetCurrentOrg,
    refreshOrganizations,
    hasPermission,
    checkFeatureAccess,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
